import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

interface VariantStats {
  campaignId: number
  totalReplies: number
  autoReply: number
  ooo: number
  humanReplies: number
  positiveReplies: number
  nullCategory: number
}

interface CampaignVariantBreakdownProps {
  isOpen: boolean
  onClose: () => void
  campaignName: string
  variantStats: VariantStats[]
  totalStats: {
    totalReplies: number
    autoReply: number
    ooo: number
    humanReplies: number
    positiveReplies: number
  }
}

export function CampaignVariantBreakdown({
  isOpen,
  onClose,
  campaignName,
  variantStats,
  totalStats
}: CampaignVariantBreakdownProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            📊 {campaignName} - Variant Breakdown
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border">
              <div className="text-2xl font-bold text-blue-600">{totalStats.totalReplies}</div>
              <div className="text-sm text-blue-600">Total Replies</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border">
              <div className="text-2xl font-bold text-red-600">{totalStats.autoReply}</div>
              <div className="text-sm text-red-600">Auto-reply</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg border">
              <div className="text-2xl font-bold text-yellow-600">{totalStats.ooo}</div>
              <div className="text-sm text-yellow-600">OOO</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border">
              <div className="text-2xl font-bold text-green-600">{totalStats.humanReplies}</div>
              <div className="text-sm text-green-600">Human Replies</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border">
              <div className="text-2xl font-bold text-purple-600">{totalStats.positiveReplies}</div>
              <div className="text-sm text-purple-600">Positive Replies</div>
            </div>
          </div>

          {/* Variant Details Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Campaign ID</TableHead>
                  <TableHead className="font-semibold text-center">Total Replies</TableHead>
                  <TableHead className="font-semibold text-center">Auto-reply</TableHead>
                  <TableHead className="font-semibold text-center">OOO</TableHead>
                  <TableHead className="font-semibold text-center">Human Replies</TableHead>
                  <TableHead className="font-semibold text-center">Positive Replies</TableHead>
                  <TableHead className="font-semibold text-center">Null Category</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variantStats.map((variant, index) => (
                  <TableRow key={variant.campaignId} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <TableCell className="font-mono text-sm">{variant.campaignId}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700">
                        {variant.totalReplies}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {variant.autoReply > 0 && (
                        <Badge variant="outline" className="bg-red-50 text-red-700">
                          {variant.autoReply}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {variant.ooo > 0 && (
                        <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                          {variant.ooo}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {variant.humanReplies > 0 && (
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {variant.humanReplies}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {variant.positiveReplies > 0 && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          {variant.positiveReplies}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {variant.nullCategory > 0 && (
                        <Badge variant="outline" className="bg-gray-50 text-gray-700">
                          {variant.nullCategory}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Calculation Breakdown */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold mb-2">📈 Calculation Breakdown:</h3>
            <div className="text-sm space-y-1">
              <div><strong>Total Replies:</strong> {totalStats.totalReplies} ({variantStats.map(v => v.totalReplies).join('+')})</div>
              <div><strong>Auto-reply:</strong> {totalStats.autoReply} ({variantStats.map(v => v.autoReply).join('+')})</div>
              <div><strong>OOO:</strong> {totalStats.ooo} ({variantStats.map(v => v.ooo).join('+')})</div>
              <div><strong>Human Replies:</strong> {totalStats.humanReplies} ({variantStats.map(v => v.humanReplies).join('+')})</div>
              <div><strong>Positive Replies:</strong> {totalStats.positiveReplies} ({variantStats.map(v => v.positiveReplies).join('+')})</div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
